import { useState } from "react";
import styles from "./admin.module.css";
import useBlogPosts from "../../hooks/useBlogPosts";
import { savePost, deletePost } from "../../data/firestore";
import { parseBodyText, blocksToText, slugify } from "../../seo/bodyText";

const EMPTY = {
  slug: "",
  title: "",
  date: new Date().toISOString().split("T")[0],
  readingMinutes: "",
  excerpt: "",
  tags: "",
  bodyText: "",
  published: true,
};

function postToForm(post) {
  return {
    slug: post.id,
    title: post.title || "",
    date: post.date || "",
    readingMinutes: post.readingMinutes || "",
    excerpt: post.excerpt || "",
    tags: (post.tags || []).join(", "),
    bodyText: blocksToText(post.body),
    published: post.published !== false,
  };
}

export default function BlogManager() {
  const { posts, loading } = useBlogPosts({ all: true });
  const [editing, setEditing] = useState(null); // null closed, EMPTY = new, form = editing
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [busySlug, setBusySlug] = useState(null);

  const startNew = () => {
    setError("");
    setEditing({ ...EMPTY });
  };

  const startEdit = (post) => {
    setError("");
    setEditing(postToForm(post));
  };

  const set = (field) => (e) =>
    setEditing((v) => ({
      ...v,
      [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const handleSave = async (e) => {
    e.preventDefault();
    const slug = editing.slug.trim() || slugify(editing.title);
    if (!slug || !editing.title.trim()) {
      setError("Title is required.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await savePost(slug, {
        title: editing.title.trim(),
        date: editing.date,
        readingMinutes: editing.readingMinutes
          ? Number(editing.readingMinutes)
          : null,
        excerpt: editing.excerpt.trim(),
        tags: editing.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        body: parseBodyText(editing.bodyText),
        published: editing.published,
      });
      setEditing(null);
    } catch {
      setError("Could not save — check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This can't be undone.`)) return;
    setBusySlug(slug);
    try {
      await deletePost(slug);
    } finally {
      setBusySlug(null);
    }
  };

  return (
    <div>
      <div className={styles.head}>
        <h2 className={styles.heading}>Blog posts</h2>
        {!editing && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={startNew}
          >
            + New post
          </button>
        )}
      </div>

      {editing && (
        <form className={styles.form} onSubmit={handleSave}>
          <div className={`${styles.formRow} ${styles.formRow2}`}>
            <div className={styles.field}>
              <label htmlFor="p-title">Title</label>
              <input
                id="p-title"
                value={editing.title}
                onChange={set("title")}
                required
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="p-slug">
                URL slug{" "}
                <span className={styles.hint}>(leave blank to auto-generate)</span>
              </label>
              <input
                id="p-slug"
                value={editing.slug}
                onChange={set("slug")}
                placeholder={slugify(editing.title) || "why-back-pain-returns"}
              />
            </div>
          </div>

          <div className={`${styles.formRow} ${styles.formRow3}`}>
            <div className={styles.field}>
              <label htmlFor="p-date">Date</label>
              <input
                id="p-date"
                type="date"
                value={editing.date}
                onChange={set("date")}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="p-mins">Reading minutes</label>
              <input
                id="p-mins"
                type="number"
                min="1"
                value={editing.readingMinutes}
                onChange={set("readingMinutes")}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="p-tags">Tags, comma separated</label>
              <input id="p-tags" value={editing.tags} onChange={set("tags")} />
            </div>
          </div>

          <div className={styles.field}>
            <label htmlFor="p-excerpt">Excerpt (shown on cards and previews)</label>
            <textarea
              id="p-excerpt"
              rows={2}
              value={editing.excerpt}
              onChange={set("excerpt")}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="p-body">Body</label>
            <p className={styles.hint}>
              Blank line = new paragraph. "## " = heading, "&gt; " = pull
              quote, "- " = list item.
            </p>
            <textarea
              id="p-body"
              className={styles.mono}
              rows={12}
              value={editing.bodyText}
              onChange={set("bodyText")}
            />
          </div>

          <div className={styles.checkRow}>
            <input
              id="p-published"
              type="checkbox"
              checked={editing.published}
              onChange={set("published")}
            />
            <label htmlFor="p-published">
              Published (visible on the live site)
            </label>
          </div>

          {error && <p className={`${styles.status} ${styles.statusErr}`}>{error}</p>}

          <div className={styles.formActions}>
            <button
              type="submit"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save post"}
            </button>
            <button
              type="button"
              className={styles.btn}
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <p className={styles.hint}>Loading…</p>}
      {!loading && posts.length === 0 && !editing && (
        <div className={styles.empty}>No posts yet — create the first one.</div>
      )}

      <ul className={styles.list}>
        {posts.map((post) => (
          <li key={post.id} className={styles.row}>
            <div className={styles.rowBody}>
              <p className={styles.rowTitle}>
                {post.title}{" "}
                {post.published === false && (
                  <span className={styles.hint}>(draft)</span>
                )}
              </p>
              <p className={styles.rowMeta}>{post.date} · /{post.id}</p>
              <p className={styles.rowText}>{post.excerpt}</p>
            </div>
            <div className={styles.rowActions}>
              <button
                type="button"
                className={styles.btn}
                onClick={() => startEdit(post)}
              >
                Edit
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={() => handleDelete(post.id)}
                disabled={busySlug === post.id}
              >
                {busySlug === post.id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
